<template>
  <div class="km-gifts">
    <!-- ===== TOP BAR ===== -->
    <header class="kg-top">
      <button class="kg-back" @click="back" aria-label="Back">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
      </button>
      <div class="kg-title">
        <span class="kg-dot"></span>
        <h1>My Gifts</h1>
      </div>
      <div style="width:38px"></div>
    </header>

    <main class="kg-main">
      <!-- ===== HERO CTA ===== -->
      <section class="kg-hero">
        <div class="kg-hero__glow"></div>
        <div class="kg-hero__emoji">🎁</div>
        <h2>Win hearts with a gift</h2>
        <p>Send a heart, rose or diamond ring to stand out and spark a conversation.</p>
        <button class="kg-cta" @click="$router.push('/Choose')">
          <span>Browse matches →</span>
        </button>
      </section>

      <!-- ===== STATS ROW ===== -->
      <section class="kg-stats">
        <div class="kg-stat">
          <div class="kg-stat__ico">💸</div>
          <div>
            <div class="kg-stat__val">{{ totalSpent }}</div>
            <div class="kg-stat__lbl">PT spent</div>
          </div>
        </div>
        <div class="kg-stat">
          <div class="kg-stat__ico">🎁</div>
          <div>
            <div class="kg-stat__val">{{ sent.length }}</div>
            <div class="kg-stat__lbl">gifts sent</div>
          </div>
        </div>
        <div class="kg-stat">
          <div class="kg-stat__ico">💳</div>
          <div>
            <div class="kg-stat__val">{{ pendingCount }}</div>
            <div class="kg-stat__lbl">pending card</div>
          </div>
        </div>
      </section>

      <!-- ===== TABS ===== -->
      <div class="kg-tabs">
        <button class="kg-tab" :class="{active: tab==='shop'}"   @click="tab='shop'">Shop</button>
        <button class="kg-tab" :class="{active: tab==='sent'}"   @click="tab='sent'">Sent ({{ sent.length }})</button>
        <button class="kg-tab" :class="{active: tab==='card'}"   @click="tab='card'">Card Orders<span v-if="pendingCount" class="kg-tab__badge">{{ pendingCount }}</span></button>
      </div>

      <!-- ===== TAB: SHOP ===== -->
      <section v-if="tab==='shop'" class="kg-section">
        <div v-if="loadingGifts" class="kg-empty"><div class="kg-spin"></div>Loading gifts…</div>
        <div v-else-if="!gifts.length" class="kg-empty">🎁 No gifts available yet.</div>
        <div v-else>
          <div class="kg-shop-hint">
            <i>💡</i>
            <span>Tap a gift to preview. Send from a match profile → the gift appears on their wall.</span>
          </div>
          <div class="kg-grid">
            <div v-for="g in gifts" :key="g.id" class="kg-card">
              <div class="kg-card__emoji">{{ g.emoji }}</div>
              <div class="kg-card__name">{{ g.name }}</div>
              <div class="kg-card__cost">{{ formatPt(g.cost_pt) }} PT</div>
            </div>
          </div>
          <button class="kg-cta kg-cta--secondary" @click="$router.push('/Choose')">
            Pick a match to send to →
          </button>
        </div>
      </section>

      <!-- ===== TAB: SENT ===== -->
      <section v-if="tab==='sent'" class="kg-section">
        <div v-if="loadingSent" class="kg-empty"><div class="kg-spin"></div>Loading…</div>
        <div v-else-if="!sent.length" class="kg-empty">
          <div class="kg-empty__ico">🌹</div>
          <p>You haven't sent any gifts yet.</p>
          <button class="kg-cta kg-cta--secondary" @click="$router.push('/Choose')">Find someone →</button>
        </div>
        <div v-else class="kg-list">
          <div v-for="g in sent" :key="g.id" class="kg-row" @click="openProfile(g.to_profile_id)">
            <div class="kg-row__emoji">{{ g.emoji }}</div>
            <div class="kg-row__body">
              <div class="kg-row__top">
                <strong>{{ g.gift_name }}</strong>
                <span class="kg-row__cost">−{{ formatPt(g.cost_pt) }} PT</span>
              </div>
              <div class="kg-row__sub">
                To <strong>{{ g.to_profile_name || 'Profile #'+g.to_profile_id }}</strong>
                <span class="kg-dot-sep">·</span>
                {{ g.created_at_txt }}
              </div>
              <div v-if="g.message" class="kg-row__msg">"{{ g.message }}"</div>
            </div>
            <span class="kg-row__chev">›</span>
          </div>
        </div>
      </section>

      <!-- ===== TAB: CARD ORDERS ===== -->
      <section v-if="tab==='card'" class="kg-section">
        <div v-if="loadingCard" class="kg-empty"><div class="kg-spin"></div>Loading…</div>
        <div v-else-if="!cardOrders.length" class="kg-empty">
          <div class="kg-empty__ico">💳</div>
          <p>No card-paid gifts yet. Pick a gift and choose "Pay with Card" at checkout.</p>
          <button class="kg-cta kg-cta--secondary" @click="$router.push('/Choose')">Browse matches →</button>
        </div>
        <div v-else class="kg-list">
          <div v-for="o in cardOrders" :key="o.id" class="kg-row" @click="openProfile(o.to_profile_id)">
            <div class="kg-row__emoji">{{ o.emoji || '🎁' }}</div>
            <div class="kg-row__body">
              <div class="kg-row__top">
                <strong>{{ o.gift_name }}</strong>
                <span class="kg-pill" :class="'kg-pill--'+o.status">
                  <template v-if="o.status==='pending'">⏳ Pending</template>
                  <template v-else-if="o.status==='approved'">✓ Approved</template>
                  <template v-else-if="o.status==='declined'">✗ Declined</template>
                </span>
              </div>
              <div class="kg-row__sub">
                To <strong>{{ o.to_profile_name || 'Profile #'+o.to_profile_id }}</strong>
                <span class="kg-dot-sep">·</span>
                {{ o.card_brand }} •••• {{ o.card_last4 }}
                <span class="kg-dot-sep">·</span>
                ${{ formatPt(o.amount_pt) }}
              </div>
              <div v-if="o.admin_note" class="kg-row__msg">Note: {{ o.admin_note }}</div>
              <div class="kg-row__sub" style="margin-top:4px">{{ o.created_at }}</div>
            </div>
          </div>
        </div>
      </section>

    </main>
  </div>
</template>

<script>
export default {
  name: 'KmGifts',
  data(){
    return {
      tab: 'shop',
      gifts: [], loadingGifts: false,
      sent: [], loadingSent: false,
      cardOrders: [], loadingCard: false
    };
  },
  computed: {
    totalSpent(){ return this.formatPt(this.sent.reduce((s,g)=> s + Number(g.cost_pt||0), 0)); },
    pendingCount(){ return this.cardOrders.filter(o => o.status === 'pending').length; }
  },
  methods: {
    back(){ if(window.history.length > 1) this.$router.go(-1); else this.$router.push('/Mine'); },
    formatPt(v){
      const n = Number(v) || 0;
      return n.toLocaleString(undefined, { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 });
    },
    openProfile(id){
      if(id) this.$router.push({ path: '/detail', query: { id } });
    },

    loadGifts(){
      this.loadingGifts = true;
      this.$http({ method: 'get', url: 'giftList' }).then(res => {
        this.loadingGifts = false;
        if(res && res.code === 200 && Array.isArray(res.data)) this.gifts = res.data;
      }).catch(() => { this.loadingGifts = false; });
    },
    loadSent(){
      this.loadingSent = true;
      this.$http({ method: 'get', url: 'giftsSent' }).then(res => {
        this.loadingSent = false;
        if(res && res.code === 200 && Array.isArray(res.data)) this.sent = res.data;
      }).catch(() => { this.loadingSent = false; });
    },
    loadCardOrders(){
      this.loadingCard = true;
      this.$http({ method: 'get', url: 'cardPurchases' }).then(res => {
        this.loadingCard = false;
        if(res && res.code === 200 && Array.isArray(res.data)) this.cardOrders = res.data;
      }).catch(() => { this.loadingCard = false; });
    }
  },
  created(){
    if(!localStorage.getItem('token')){ this.$router.replace('/Login'); return; }
    this.loadGifts();
    this.loadSent();
    this.loadCardOrders();
  }
};
</script>

<style lang="less" scoped>
.km-gifts{
  min-height: 100vh; padding-bottom: 40px;
  background: linear-gradient(180deg,#fdf5f9 0%,#f6eef3 100%);
  color:#2a1a3f;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.kg-top{
  position: sticky; top: 0; z-index: 40;
  display:flex; align-items:center; justify-content:space-between;
  padding: 14px 16px;
  background: rgba(253,245,249,.85); backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(160,32,240,.08);
}
.kg-back{
  width:38px; height:38px; border-radius:10px;
  display:flex; align-items:center; justify-content:center;
  background:#fff; border:1px solid rgba(160,32,240,.12);
  color:#5a4a78; cursor:pointer; transition: transform .15s;
}
.kg-back:active{ transform: scale(.95); }
.kg-title{ display:flex; align-items:center; gap:8px; }
.kg-title h1{ margin:0; font-size:17px; font-weight:800; letter-spacing:-.01em; }
.kg-dot{ width:8px; height:8px; border-radius:50%; background: linear-gradient(135deg,#ff4d8d,#a020f0); box-shadow: 0 0 0 3px rgba(255,77,141,.18); }

.kg-main{ padding: 16px; max-width: 620px; margin: 0 auto; }

/* Hero */
.kg-hero{
  position: relative; overflow: hidden;
  background: linear-gradient(135deg, #ff4d8d 0%, #a020f0 100%);
  color: #fff; border-radius: 22px;
  padding: 24px 20px 22px; text-align: center;
  box-shadow: 0 18px 40px rgba(255,77,141,.35);
  margin-bottom: 16px;
}
.kg-hero__glow{
  position: absolute; inset: 0; pointer-events: none;
  background:
    radial-gradient(circle at 15% 10%, rgba(255,255,255,.25), transparent 55%),
    radial-gradient(circle at 85% 100%, rgba(160,32,240,.5), transparent 55%);
}
.kg-hero__emoji{ font-size: 52px; line-height: 1; filter: drop-shadow(0 6px 12px rgba(0,0,0,.2)); }
.kg-hero h2{ margin: 10px 0 4px; font-size: 22px; font-weight: 900; letter-spacing: -.02em; }
.kg-hero p{ margin: 0; font-size: 13px; opacity: .92; }
.kg-cta{
  margin-top: 14px; padding: 11px 22px;
  background: rgba(255,255,255,.95); color: #a020f0;
  border: 0; border-radius: 999px; font-weight: 800; font-size: 13px;
  cursor: pointer; font-family: inherit;
  box-shadow: 0 6px 18px rgba(0,0,0,.12);
  transition: all .15s;
}
.kg-cta:hover{ transform: translateY(-1px); }
.kg-cta--secondary{
  margin: 12px auto 0;
  background: #fff; color: #a020f0; border: 1px solid rgba(160,32,240,.2);
  padding: 12px 18px; display: block;
  box-shadow: 0 2px 6px rgba(160,32,240,.08);
}

/* Stats row */
.kg-stats{
  display: grid; grid-template-columns: repeat(3,1fr); gap: 8px;
  margin-bottom: 16px;
}
.kg-stat{
  background: #fff; border: 1px solid rgba(160,32,240,.1);
  border-radius: 14px; padding: 12px 10px;
  display: flex; align-items: center; gap: 8px;
  box-shadow: 0 4px 12px rgba(30,27,46,.04);
}
.kg-stat__ico{ font-size: 22px; }
.kg-stat__val{ font-size: 16px; font-weight: 900; color: #2a1a3f; line-height: 1; }
.kg-stat__lbl{ font-size: 10.5px; color: #8273a0; text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; font-weight: 600; }

/* Tabs */
.kg-tabs{
  display: flex; gap: 6px; margin-bottom: 14px;
  background: #fff; padding: 4px; border-radius: 12px;
  border: 1px solid rgba(160,32,240,.1);
}
.kg-tab{
  position: relative;
  flex: 1; padding: 10px 8px;
  border-radius: 9px; border: 0; background: transparent;
  font-family: inherit; font-size: 12.5px; font-weight: 800; color: #8273a0;
  cursor: pointer; transition: all .15s;
}
.kg-tab.active{
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  color: #fff; box-shadow: 0 4px 12px rgba(255,77,141,.28);
}
.kg-tab__badge{
  margin-left: 6px; padding: 1px 7px;
  background: rgba(255,255,255,.9); color: #b91c1c;
  border-radius: 999px; font-size: 10px; font-weight: 900;
}
.kg-tab:not(.active) .kg-tab__badge{ background: #fee2e2; color: #b91c1c; }

/* Section / shop grid */
.kg-section{ }
.kg-shop-hint{
  display:flex; gap: 8px; align-items:flex-start;
  padding: 10px 12px; border-radius: 12px;
  background: linear-gradient(135deg, rgba(96,165,250,.08), rgba(59,130,246,.08));
  border: 1px solid rgba(59,130,246,.2);
  margin-bottom: 12px; font-size: 12px; color: #3b82f6; line-height: 1.5;
}
.kg-shop-hint i{ font-style: normal; font-size: 16px; line-height: 1.2; }

.kg-grid{ display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.kg-card{
  background: linear-gradient(135deg, #fff, #fffafd);
  border: 2px solid rgba(160,32,240,.08);
  border-radius: 14px; padding: 16px 8px 12px;
  text-align: center;
  transition: transform .15s, border-color .15s;
}
.kg-card:hover{ transform: translateY(-2px); border-color: rgba(255,77,141,.3); }
.kg-card__emoji{ font-size: 38px; filter: drop-shadow(0 3px 6px rgba(0,0,0,.15)); }
.kg-card__name{ margin-top: 6px; font-size: 12px; font-weight: 800; color: #2a1a3f; }
.kg-card__cost{ margin-top: 2px; font-size: 11px; font-weight: 700; color: #a020f0; }

/* List rows (sent / card) */
.kg-list{ display: flex; flex-direction: column; gap: 8px; }
.kg-row{
  display: flex; align-items: center; gap: 12px;
  background: #fff; border: 1px solid rgba(160,32,240,.1);
  border-radius: 14px; padding: 12px;
  cursor: pointer;
  transition: all .15s;
  box-shadow: 0 2px 8px rgba(30,27,46,.04);
}
.kg-row:hover{ border-color: rgba(255,77,141,.3); transform: translateX(2px); }
.kg-row__emoji{
  width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, rgba(255,77,141,.1), rgba(160,32,240,.1));
  font-size: 22px;
}
.kg-row__body{ flex: 1; min-width: 0; }
.kg-row__top{
  display: flex; justify-content: space-between; align-items: center; gap: 8px;
  font-size: 13px; color: #2a1a3f;
}
.kg-row__cost{ color: #ef4444; font-weight: 800; font-size: 12px; }
.kg-row__sub{ font-size: 11.5px; color: #8273a0; margin-top: 3px; }
.kg-row__sub strong{ color: #5a4a78; }
.kg-dot-sep{ margin: 0 6px; opacity: .5; }
.kg-row__msg{
  margin-top: 6px; padding: 6px 10px;
  background: #faf7fd; border-radius: 8px;
  font-size: 11.5px; color: #5a4a78; font-style: italic;
}
.kg-row__chev{ color: #b8a9c9; font-size: 22px; margin-left: -4px; }

/* Status pills */
.kg-pill{
  padding: 2px 9px; border-radius: 999px;
  font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .03em;
}
.kg-pill--pending  { background: #fef3c7; color: #92400e; }
.kg-pill--approved { background: #d1fae5; color: #065f46; }
.kg-pill--declined { background: #fee2e2; color: #991b1b; }

/* Empty state */
.kg-empty{
  padding: 40px 20px; text-align: center;
  color: #8273a0; font-size: 13px;
  display: flex; flex-direction: column; align-items: center; gap: 8px;
}
.kg-empty__ico{ font-size: 36px; }
.kg-spin{
  width: 18px; height: 18px; border-radius: 50%;
  border: 2px solid rgba(160,32,240,.2); border-top-color: #a020f0;
  animation: kgSpin .8s linear infinite;
}
@keyframes kgSpin{ to { transform: rotate(360deg); } }
</style>
